import { Boxes, LayoutGrid, Network } from 'lucide-react';
import { useState } from 'react';

import { useGroups } from '@/react/portainer/environments/environment-groups/queries';
import {
  useEdgeGroups,
  EdgeGroupListItemResponse,
} from '@/react/edge/edge-groups/queries/useEdgeGroups';
import { pluralize } from '@/react/common/string-utils';
import { EnvironmentType } from '@/react/portainer/environments/types';
import { EdgeEnvironmentsAssociationTable } from '@/react/edge/components/EdgeEnvironmentsAssociationTable';

import { Checkbox } from '@@/form-components/Checkbox';
import { Icon } from '@@/Icon';
import { Loading } from '@@/Widget';
import { Card } from '@@/primitives/Card';
import { CollapseExpandButton } from '@@/CollapseExpandButton';

import { ArtifactTargets } from '../../types';

/** The only environments this engine can reconcile. */
const RECONCILABLE_TYPES = [EnvironmentType.EdgeAgentOnKubernetes] as const;

/**
 * Picks what a reconciled deployment is applied to.
 *
 * Selecting a group stores the group, not the environments in it, so a fleet
 * that grows into the group is deployed to without the workflow being edited.
 * Naming an environment stores the environment, and is the exception rather
 * than the rule - which is why it sits in a section of its own, closed until
 * it is wanted, rather than nested inside the groups.
 */
export function TargetsTree({
  value,
  onChange,
}: {
  value: ArtifactTargets;
  onChange: (next: ArtifactTargets) => void;
}) {
  return (
    <Card.Container aria-label="Deployment targets">
      <Card.Header
        icon={LayoutGrid}
        title="Deployment targets"
        actions={<Selection value={value} />}
      />
      <Card.Body>
        <div className="flex flex-col gap-4">
          <EdgeGroups value={value} onChange={onChange} />
          <EnvironmentGroups value={value} onChange={onChange} />
          <Environments value={value} onChange={onChange} />
        </div>
      </Card.Body>
    </Card.Container>
  );
}

/** What has been picked, counted by kind rather than resolved. */
function Selection({ value }: { value: ArtifactTargets }) {
  const parts = [
    countLabel(value.edgeGroups.length, 'Edge group'),
    countLabel(value.environmentGroups.length, 'environment group'),
    countLabel(value.environments.length, 'environment'),
  ].filter(Boolean);

  if (!parts.length) {
    return <span className="text-muted text-xs">Nothing selected</span>;
  }

  return <span className="text-xs">{parts.join(', ')}</span>;
}

/** What an Edge group contributes, and why it may contribute nothing. */
function edgeGroupNote(
  group: EdgeGroupListItemResponse,
  reconcilable: boolean
): string {
  if (!reconcilable) {
    return 'No Edge Kubernetes environments';
  }

  // Exact numbers per type are not on this response, so a mixed group says that
  // rather than implying the whole group is deployed to.
  if (group.EndpointTypes.length > 1) {
    return 'Only its Edge Kubernetes environments';
  }

  return `${group.EndpointsCount} ${pluralize(group.EndpointsCount, 'environment')}`;
}

/** One selectable target, with what it reaches beside it. */
function Row({
  id,
  dataCy,
  name,
  checked,
  disabled,
  note,
  onChange,
}: {
  id: string;
  dataCy: string;
  name: string;
  checked: boolean;
  disabled?: boolean;
  note?: string;
  onChange: () => void;
}) {
  return (
    <div className="flex items-center gap-2">
      <Checkbox
        id={id}
        data-cy={dataCy}
        label={name}
        checked={checked}
        disabled={disabled}
        onChange={onChange}
      />
      {note && <span className="text-muted text-xs">{note}</span>}
    </div>
  );
}

function countLabel(count: number, noun: string): string {
  if (count === 0) {
    return '';
  }

  return `${count} ${pluralize(count, noun)}`;
}

function EdgeGroups({
  value,
  onChange,
}: {
  value: ArtifactTargets;
  onChange: (next: ArtifactTargets) => void;
}) {
  const { data: groups, isLoading } = useEdgeGroups({ summarize: true });

  return (
    <Section
      title="Edge groups"
      icon={Network}
      subtitle="Membership follows the Edge group, including its tag rules."
    >
      {isLoading && <Loading />}

      {!isLoading && !groups?.length && (
        <span className="text-muted text-xs">No Edge groups available.</span>
      )}

      {groups?.map((group) => {
        // The engine reconciles Edge Kubernetes environments and skips the rest,
        // so a group holding none reaches nothing and the server refuses it.
        const reconcilable = group.EndpointTypes.includes(
          EnvironmentType.EdgeAgentOnKubernetes
        );

        return (
          <Row
            key={group.Id}
            id={`target-edge-group-${group.Id}`}
            dataCy={`workflow-target-edge-group-${group.Id}`}
            name={group.Name}
            checked={value.edgeGroups.includes(group.Id)}
            disabled={!reconcilable}
            note={edgeGroupNote(group, reconcilable)}
            onChange={() =>
              onChange({
                ...value,
                edgeGroups: toggleSelection(value.edgeGroups, group.Id),
              })
            }
          />
        );
      })}
    </Section>
  );
}

function EnvironmentGroups({
  value,
  onChange,
}: {
  value: ArtifactTargets;
  onChange: (next: ArtifactTargets) => void;
}) {
  const { data: groups, isLoading } = useGroups();

  return (
    <Section
      title="Environment groups"
      icon={LayoutGrid}
      subtitle="Membership follows the group. Only its Edge Kubernetes environments are reconciled."
    >
      {isLoading && <Loading />}

      {!isLoading && !groups?.length && (
        <span className="text-muted text-xs">
          No environment groups available.
        </span>
      )}

      {/* Wrapped rather than one per line: a fleet's groups are short names,
          and a list of them should not push the environments below off the
          screen. Wrapped rather than a grid, too, so two groups sit beside each
          other instead of at opposite ends of an equal-width column. */}
      <div className="flex flex-wrap gap-x-6 gap-y-1">
        {groups?.map((group) => (
          <Row
            key={group.Id}
            id={`target-environment-group-${group.Id}`}
            dataCy={`workflow-target-environment-group-${group.Id}`}
            name={group.Name}
            checked={value.environmentGroups.includes(group.Id)}
            onChange={() =>
              onChange({
                ...value,
                environmentGroups: toggleSelection(
                  value.environmentGroups,
                  group.Id
                ),
              })
            }
          />
        ))}
      </div>
    </Section>
  );
}

/**
 * Names individual environments, through the same pair of tables an Edge group
 * is built with.
 *
 * Read a page at a time and searched on the server, because a fleet is the one
 * thing here with no upper bound - and closed to begin with, because most
 * deployments follow a group and never open it.
 *
 * An environment a followed group already reaches can still be named. That is
 * redundant rather than wrong: the server takes the union of what the targets
 * resolve to, and nothing here can say which group an environment falls in
 * without reading the whole fleet back, which is what this replaces.
 */
function Environments({
  value,
  onChange,
}: {
  value: ArtifactTargets;
  onChange: (next: ArtifactTargets) => void;
}) {
  const [isExpanded, setIsExpanded] = useState(value.environments.length > 0);

  return (
    <div className="flex flex-col gap-2">
      <div className="flex items-center gap-2">
        <CollapseExpandButton
          isExpanded={isExpanded}
          onClick={() => setIsExpanded(!isExpanded)}
          data-cy="workflow-target-environments-toggle"
        />
        <Icon icon={Boxes} size="sm" className="text-gray-6" />
        <span className="text-sm font-medium">Environments</span>
        <span className="text-muted text-xs">
          {value.environments.length
            ? `${countLabel(value.environments.length, 'environment')} named`
            : 'Optional'}
        </span>
      </div>

      {isExpanded && (
        <>
          <span className="text-muted text-xs">
            Name environments to deploy to whatever group they are in. Click a
            row to move it between the tables.
          </span>

          <div className="flex flex-col gap-4 lg:flex-row">
            <div className="lg:w-1/2">
              <EdgeEnvironmentsAssociationTable
                title="Available environments"
                query={{
                  types: RECONCILABLE_TYPES,
                  excludeIds: value.environments,
                }}
                onClickRow={(environment) =>
                  onChange({
                    ...value,
                    environments: [...value.environments, environment.Id],
                  })
                }
                data-cy="workflow-target-available-environments"
                noWidget
              />
            </div>
            <div className="lg:w-1/2">
              <EdgeEnvironmentsAssociationTable
                title="Named environments"
                query={{
                  types: RECONCILABLE_TYPES,
                  endpointIds: value.environments,
                }}
                onClickRow={(environment) =>
                  onChange({
                    ...value,
                    environments: value.environments.filter(
                      (id) => id !== environment.Id
                    ),
                  })
                }
                data-cy="workflow-target-named-environments"
                noWidget
              />
            </div>
          </div>
        </>
      )}
    </div>
  );
}

function Section({
  title,
  subtitle,
  icon,
  children,
}: {
  title: string;
  subtitle: string;
  icon: typeof LayoutGrid;
  children: React.ReactNode;
}) {
  return (
    <div className="flex flex-col gap-2">
      <div className="flex items-center gap-2">
        <Icon icon={icon} size="sm" className="text-gray-6" />
        <span className="text-sm font-medium">{title}</span>
      </div>
      <span className="text-muted text-xs">{subtitle}</span>
      <div className="flex flex-col gap-1">{children}</div>
    </div>
  );
}

function toggleSelection<T>(values: Array<T>, value: T): Array<T> {
  return values.includes(value)
    ? values.filter((current) => current !== value)
    : [...values, value];
}
